import type {
	PrescriptionSelectionCourse,
	PrescriptionSelectionFocusAreaSelector,
	PrescriptionSelectionOptionGroup,
	PrescriptionSelectionRequirementsRule,
	PrescriptionSelectionWork,
} from '@/types/fetchPrescriptionSelection'
import sanitizeHtml from 'sanitize-html'

// TODO TEMPORARY — per-course validation allow-list.
const VALIDATED_COURSE_PREFIXES: readonly string[] = [
	'English Standard',
	'English EAL/D',
	'English Advanced',
	'English Studies',
	'English Extension 1',
	'English Extension 2',
] as const

export type ElectiveSelectionState = {
	[courseTitle: string]: number | undefined
}

const isValidationEnabledForCourse = (
	course: PrescriptionSelectionCourse,
): boolean => {
	const lower = course['course-title'].toLowerCase()
	return VALIDATED_COURSE_PREFIXES.some((prefix) =>
		lower.startsWith(prefix.toLowerCase()),
	)
}

export type CourseStatus = 'optional' | 'incomplete' | 'complete'

export interface SelectionSlot {
	workIds: string[]
}

export type CourseSelections = {
	[selectorIndex: number]: SelectionSlot | undefined
}

export type SelectionState = {
	[courseTitle: string]: CourseSelections | undefined
}

export const COURSE_DISPLAY_ORDER: readonly string[] = [
	'English Studies',
	'English Standard',
	'English Advanced',
	'English EAL/D',
	'English Extension 1',
	'English Extension 2',
] as const

const courseOrderIndex = (courseTitle: string): number => {
	const lower = courseTitle.toLowerCase()
	for (let i = 0; i < COURSE_DISPLAY_ORDER.length; i++) {
		if (lower.startsWith(COURSE_DISPLAY_ORDER[i].toLowerCase())) {
			return i
		}
	}
	return Number.POSITIVE_INFINITY
}

export const sortCoursesByDisplayOrder = (
	courses: PrescriptionSelectionCourse[],
): PrescriptionSelectionCourse[] =>
	courses
		.map((course, index) => ({ course, index }))
		.sort((a, b) => {
			const aIdx = courseOrderIndex(a.course['course-title'])
			const bIdx = courseOrderIndex(b.course['course-title'])
			if (aIdx !== bIdx) return aIdx - bIdx
			return a.index - b.index
		})
		.map((entry) => entry.course)

export const getCourseOptionGroups = (
	course: PrescriptionSelectionCourse,
): PrescriptionSelectionOptionGroup[] =>
	course.selectors?.[0]?.['option-groups'] ?? []

export const isStudiesVariant = (
	course: PrescriptionSelectionCourse,
): boolean => {
	const firstStudies = getCourseOptionGroups(course)[0]?.studies
	return Array.isArray(firstStudies) && firstStudies.length > 0
}

export const isElectiveCourse = (
	course: PrescriptionSelectionCourse,
): boolean => {
	const optionGroups = getCourseOptionGroups(course)
	if (optionGroups.length === 0) return false
	return optionGroups.every(
		(og) =>
			typeof og.option === 'string' &&
			og.option.length > 0 &&
			Array.isArray(og.groups) &&
			og.groups.length > 0,
	)
}

export const resolveCourseWithElective = (
	course: PrescriptionSelectionCourse,
	electiveOptionIndex: number | undefined,
): PrescriptionSelectionCourse => {
	if (!isElectiveCourse(course)) return course
	if (electiveOptionIndex === undefined) return course
	const optionGroup = getCourseOptionGroups(course)[electiveOptionIndex]
	if (!optionGroup) return course
	const sourceSelectors = course.selectors ?? []
	if (sourceSelectors.length === 0) return course
	return {
		...course,
		selectors: sourceSelectors.map((selector) => ({
			...selector,
			groups: optionGroup.groups ?? [],
		})),
	}
}

const OPTIONAL_COURSE_PREFIXES: readonly string[] = [
	'English Extension 1',
	'English Extension 2',
] as const

export const isOptionalCourse = (
	course: PrescriptionSelectionCourse,
): boolean => {
	const lower = course['course-title'].toLowerCase()
	return OPTIONAL_COURSE_PREFIXES.some((prefix) =>
		lower.startsWith(prefix.toLowerCase()),
	)
}

export const stripTitleHtml = (htmlTitle: string | null | undefined): string =>
	sanitizeHtml(htmlTitle ?? '', {
		allowedTags: [],
		allowedAttributes: {},
	}).trim()

// Combine a work's author + title for single-line labels, omitting the comma
// when either part is missing (no leading ", Title" when author is empty).
export const formatWorkLabel = (
	author: string | null | undefined,
	title: string | null | undefined,
): string => {
	const a = (author ?? '').trim()
	const t = (title ?? '').trim()
	if (!a) return t
	if (!t) return a
	return `${a}, ${t}`
}

export const sanitizeTitleInlineHtml = (
	htmlTitle: string | null | undefined,
): string =>
	sanitizeHtml(htmlTitle ?? '', {
		allowedTags: ['em', 'i', 'strong', 'b', 'br'],
		allowedAttributes: {},
	}).trim()

interface CourseWorkRef {
	work: PrescriptionSelectionWork
	slotIndices: number[]
}

const STUDIES_SLOT_INDEX = 0

const parseSelectionQty = (raw: string | undefined): number => {
	if (!raw) return 1
	const n = parseInt(raw, 10)
	if (!Number.isFinite(n) || n < 1) return 1
	return n
}

export interface ExpandedSelectorSlot {
	flatSlotIndex: number
	originalSelectorIndex: number
	rowWithinSelector: number
	selector: PrescriptionSelectionFocusAreaSelector
	focusArea: string
}

export const expandSelectorsByQty = (
	course: PrescriptionSelectionCourse,
): ExpandedSelectorSlot[] => {
	const selectors = course.selectors ?? []
	const slots: ExpandedSelectorSlot[] = []
	let flatSlotIndex = 0
	selectors.forEach((selector, originalSelectorIndex) => {
		const qty = parseSelectionQty(selector['selection-qty'])
		for (
			let rowWithinSelector = 0;
			rowWithinSelector < qty;
			rowWithinSelector++
		) {
			slots.push({
				flatSlotIndex,
				originalSelectorIndex,
				rowWithinSelector,
				selector,
				focusArea: selector['focus-area-title'] ?? '',
			})
			flatSlotIndex++
		}
	})
	return slots
}

export const getSelectorForFlatSlot = (
	course: PrescriptionSelectionCourse,
	flatSlotIndex: number,
): PrescriptionSelectionFocusAreaSelector | undefined => {
	return expandSelectorsByQty(course)[flatSlotIndex]?.selector
}

const studyAsWork = (study: {
	id: string
	author: string
}): PrescriptionSelectionWork => ({
	id: study.id,
	author: study.author,
	title: '',
	subtitle: '',
	'prescription-type': '',
	'prescription-form': null,
	shakespeare: false,
	'title-link': null,
})

const buildWorkIndex = (
	course: PrescriptionSelectionCourse,
): Map<string, CourseWorkRef> => {
	const index = new Map<string, CourseWorkRef>()

	const addWork = (
		work: PrescriptionSelectionWork,
		slotIndex: number,
	): void => {
		const existing = index.get(work.id)
		if (existing) {
			if (!existing.slotIndices.includes(slotIndex)) {
				existing.slotIndices.push(slotIndex)
			}
			return
		}
		index.set(work.id, { work, slotIndices: [slotIndex] })
	}

	const expanded = expandSelectorsByQty(course)
	expanded.forEach((slot) => {
		const groups = slot.selector.groups ?? []
		groups.forEach((group) => {
			;(group.works ?? []).forEach((work) =>
				addWork(work, slot.flatSlotIndex),
			)
			;(group.bundles ?? []).forEach((bundle) => {
				;(bundle.works ?? []).forEach((work) =>
					addWork(work, slot.flatSlotIndex),
				)
			})
		})
	})

	// Variant B — fold studies in as permissive synthetic works.
	const studies = getCourseOptionGroups(course)[0]?.studies ?? []
	studies.forEach((study) => {
		addWork(studyAsWork(study), STUDIES_SLOT_INDEX)
	})

	return index
}

const workSatisfiesRule = (
	work: PrescriptionSelectionWork,
	rule: PrescriptionSelectionRequirementsRule,
): boolean => {
	const types = rule['prescription-type'] ?? []
	const forms = rule['prescription-form'] ?? []
	const typeOk =
		types.length === 0 || types.includes(work['prescription-type'])
	const formOk =
		forms.length === 0 ||
		(work['prescription-form'] !== null &&
			forms.includes(work['prescription-form']))
	return typeOk && formOk
}

// Work-once (universal) rule
const isWorkSelectedInAnotherSlot = (
	workId: string,
	excludedSlotIndex: number,
	courseSelections: CourseSelections,
): boolean => {
	for (const [slotIdxRaw, slot] of Object.entries(courseSelections)) {
		if (Number(slotIdxRaw) === excludedSlotIndex) continue
		if (!slot) continue
		if (slot.workIds.includes(workId)) return true
	}
	return false
}

const computeFulfilledRuleIndices = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections,
): Set<number> => {
	const fulfilled = new Set<number>()
	const rules =
		course['prescription-requirements']?.['requirements-rules'] ?? []
	if (rules.length === 0) return fulfilled

	const workIndex = buildWorkIndex(course)

	interface SelectedOccurrence {
		key: string
		work: PrescriptionSelectionWork
	}
	const occurrences: SelectedOccurrence[] = []
	Object.entries(courseSelections)
		.map(
			([slotIdxRaw, slot]) =>
				[Number(slotIdxRaw), slot] as [
					number,
					SelectionSlot | undefined,
				],
		)
		.sort((a, b) => a[0] - b[0])
		.forEach(([slotIndex, slot]) => {
			if (!slot) return
			slot.workIds.forEach((workId, position) => {
				const ref = workIndex.get(workId)
				if (!ref) return
				occurrences.push({
					key: `${slotIndex}:${position}`,
					work: ref.work,
				})
			})
		})

	const validated = isValidationEnabledForCourse(course)
	const consumed = new Set<string>()
	rules.forEach((rule, ruleIndex) => {
		for (const occ of occurrences) {
			if (consumed.has(occ.key)) continue
			if (validated && !workSatisfiesRule(occ.work, rule)) continue
			consumed.add(occ.key)
			fulfilled.add(ruleIndex)
			break
		}
	})

	return fulfilled
}

const SHAKESPEARE_SELECTION_RULE = 'One work by Shakespeare' as const
const SHAKESPEARE_UNMET_MESSAGE =
	'Select at least one text by Shakespeare' as const

export const courseRequiresShakespeare = (
	course: PrescriptionSelectionCourse,
): boolean => {
	const rules = course['prescription-requirements']?.['selection-rules'] ?? []
	return rules.includes(SHAKESPEARE_SELECTION_RULE)
}

export const courseRequiresForm = (
	course: PrescriptionSelectionCourse,
): boolean => {
	const rules =
		course['prescription-requirements']?.['requirements-rules'] ?? []
	return rules.some((rule) => (rule['prescription-form']?.length ?? 0) > 0)
}

export const getCourseConfirmationMessage = (
	course: PrescriptionSelectionCourse,
): string => course['prescription-requirements']?.['confirmation-message'] ?? ''

interface UnmetSelectionRule {
	rule: string
	message: string
}

const computeUnmetSelectionRules = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections,
): UnmetSelectionRule[] => {
	if (!isValidationEnabledForCourse(course)) return []
	const rules = course['prescription-requirements']?.['selection-rules'] ?? []
	if (rules.length === 0) return []

	const workIndex = buildWorkIndex(course)
	const hasShakespeare = Object.values(courseSelections).some((slot) => {
		if (!slot) return false
		return slot.workIds.some((id) => {
			const ref = workIndex.get(id)
			return ref?.work.shakespeare === true
		})
	})

	const unmet: UnmetSelectionRule[] = []
	rules.forEach((rule) => {
		if (rule === SHAKESPEARE_SELECTION_RULE) {
			if (!hasShakespeare) {
				unmet.push({ rule, message: SHAKESPEARE_UNMET_MESSAGE })
			}
			return
		}

		unmet.push({
			rule,
			message: `Select at least one work that meets: ${rule}`,
		})
	})
	return unmet
}

const isSelectionStateEmpty = (
	courseSelections: CourseSelections | undefined,
): boolean => {
	if (!courseSelections) return true
	return !Object.values(courseSelections).some(
		(slot) => slot && slot.workIds.length > 0,
	)
}

export const ELECTIVE_UNMET_MESSAGE =
	'Select an elective to load text prescriptions' as const

// Shared completion evaluator — the single definition of "this selection
// satisfies the course". Used both by `getCourseStatus` and by the look-ahead
// feasibility search, so the two can never disagree.
const areCourseRequirementsMet = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections,
): boolean => {
	const rules =
		course['prescription-requirements']?.['requirements-rules'] ?? []
	const fulfilled = computeFulfilledRuleIndices(course, courseSelections)
	const allRulesFulfilled = rules.every((_, idx) => fulfilled.has(idx))
	const allSelectionRulesMet =
		computeUnmetSelectionRules(course, courseSelections).length === 0
	return allRulesFulfilled && allSelectionRulesMet
}

export const getCourseStatus = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections | undefined,
	electiveOptionIndex?: number,
): CourseStatus => {
	const isElective = isElectiveCourse(course)
	const hasElectivePick = isElective && electiveOptionIndex !== undefined
	const hasWorkPicks = !isSelectionStateEmpty(courseSelections)

	if (!hasElectivePick && !hasWorkPicks) {
		return 'optional'
	}

	// Variant C: cannot reach `complete` without an elective picked
	// (work index would be empty so rules can never be fulfilled).
	if (isElective && !hasElectivePick) return 'incomplete'

	const resolved = isElective
		? resolveCourseWithElective(course, electiveOptionIndex)
		: course
	const safe: CourseSelections = courseSelections ?? {}

	return areCourseRequirementsMet(resolved, safe) ? 'complete' : 'incomplete'
}

export const getUnmetMessages = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections | undefined,
	electiveOptionIndex?: number,
): string[] => {
	const isElective = isElectiveCourse(course)
	if (isElective && electiveOptionIndex === undefined) {
		return [ELECTIVE_UNMET_MESSAGE]
	}

	const resolved = isElective
		? resolveCourseWithElective(course, electiveOptionIndex)
		: course
	const safe: CourseSelections = courseSelections ?? {}
	const rules =
		resolved['prescription-requirements']?.['requirements-rules'] ?? []
	const fulfilled = computeFulfilledRuleIndices(resolved, safe)

	const messages: string[] = []
	rules.forEach((rule, idx) => {
		if (!fulfilled.has(idx)) messages.push(rule.message)
	})

	computeUnmetSelectionRules(resolved, safe).forEach((entry) => {
		messages.push(entry.message)
	})
	return messages
}

// ── Generic look-ahead feasibility ──────────────────────────────────────────
// An option is disabled iff selecting it leaves zero valid completions across
// the remaining empty selector slots. This single rule covers both:
//   - Type 1: a mid-selection pick that strands a later selector, and
//   - Type 2: a final pick that meets one rule while leaving another unmet
//     (the search degenerates to a single completion check at the last slot).
// It also subsumes the previous ad-hoc "last empty slot requires Shakespeare"
// and type-redundancy heuristics.

export type CandidateDisabledReason = 'work-once' | 'no-valid-completion'

export interface CandidateAvailability {
	disabled: boolean
	reason?: CandidateDisabledReason
}

interface SlotOption {
	workIds: string[]
}

// Flat-slot index -> every standalone work and every bundle, as work-id lists.
// Branched over by the feasibility search; uses RAW options (not disabled-state
// helpers) so the search never becomes circular.
const buildSlotOptions = (
	course: PrescriptionSelectionCourse,
): Map<number, SlotOption[]> => {
	const map = new Map<number, SlotOption[]>()
	expandSelectorsByQty(course).forEach((slot) => {
		const options: SlotOption[] = []
		;(slot.selector.groups ?? []).forEach((group) => {
			;(group.works ?? []).forEach((work) =>
				options.push({ workIds: [work.id] }),
			)
			;(group.bundles ?? []).forEach((bundle) => {
				const ids = (bundle.works ?? []).map((w) => w.id)
				if (ids.length > 0) options.push({ workIds: ids })
			})
		})
		map.set(slot.flatSlotIndex, options)
	})
	return map
}

// Backtracking search: does any assignment of the still-empty slots complete the
// course? `selections` already has the tentative candidate placed in its slot.
const hasValidCompletion = (
	course: PrescriptionSelectionCourse,
	selections: CourseSelections,
	slotOptions: Map<number, SlotOption[]>,
): boolean => {
	const emptySlots = [...slotOptions.keys()].filter((i) => {
		const slot = selections[i]
		return !slot || slot.workIds.length === 0
	})

	const usedIds = new Set<string>()
	Object.values(selections).forEach((slot) =>
		slot?.workIds.forEach((id) => usedIds.add(id)),
	)

	const search = (k: number): boolean => {
		if (k === emptySlots.length) {
			return areCourseRequirementsMet(course, selections)
		}
		const slotIndex = emptySlots[k]
		for (const option of slotOptions.get(slotIndex) ?? []) {
			// Work-once — a work id may not be reused across slots.
			if (option.workIds.some((id) => usedIds.has(id))) continue
			selections[slotIndex] = { workIds: option.workIds }
			option.workIds.forEach((id) => usedIds.add(id))
			if (search(k + 1)) {
				delete selections[slotIndex]
				option.workIds.forEach((id) => usedIds.delete(id))
				return true
			}
			delete selections[slotIndex]
			option.workIds.forEach((id) => usedIds.delete(id))
		}
		return false
	}
	return search(0)
}

// Candidate-level availability — the single source of truth for disabling an
// option (a standalone work is just a one-id candidate; a bundle is its inner
// ids). Returns metadata so callers can surface a debug `data-disabled-reason`.
export const getCandidateAvailabilityInSlot = (
	candidateWorkIds: string[],
	slotIndex: number,
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections | undefined,
): CandidateAvailability => {
	if (candidateWorkIds.length === 0) return { disabled: false }
	const safe: CourseSelections = courseSelections ?? {}

	// Current selection in this slot is always re-enabled (swap UX). Checked
	// first so it survives every disable rule below.
	const currentSlot = safe[slotIndex]
	if (
		currentSlot &&
		currentSlot.workIds.length === candidateWorkIds.length &&
		candidateWorkIds.every((id) => currentSlot.workIds.includes(id))
	) {
		return { disabled: false }
	}

	// Validation bypass — preserves the per-course allow-list.
	if (!isValidationEnabledForCourse(course)) return { disabled: false }
	// Studies variant has a single slot — no cross-slot interaction.
	if (isStudiesVariant(course)) return { disabled: false }

	// Work-once — picking the same work id in another slot disables it elsewhere.
	// (Also enforced inside the search; kept here as an explicit fast-path/reason.)
	for (const id of candidateWorkIds) {
		if (isWorkSelectedInAnotherSlot(id, slotIndex, safe)) {
			return { disabled: true, reason: 'work-once' }
		}
	}

	// Look-ahead — place the candidate, then ask whether any completion remains.
	const trial: CourseSelections = {
		...safe,
		[slotIndex]: { workIds: candidateWorkIds },
	}
	if (!hasValidCompletion(course, trial, buildSlotOptions(course))) {
		return { disabled: true, reason: 'no-valid-completion' }
	}
	return { disabled: false }
}

export interface ResolvedSelectionEntry {
	slotIndex: number
	work: PrescriptionSelectionWork
}

export const resolveCourseSelections = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections | undefined,
): ResolvedSelectionEntry[] => {
	if (!courseSelections) return []
	const workIndex = buildWorkIndex(course)
	const entries: ResolvedSelectionEntry[] = []
	Object.entries(courseSelections).forEach(([slotIndexRaw, slot]) => {
		if (!slot) return
		const slotIndex = Number(slotIndexRaw)
		slot.workIds.forEach((workId) => {
			const ref = workIndex.get(workId)
			if (ref) entries.push({ slotIndex, work: ref.work })
		})
	})
	return entries
}

export const resolveStudySelection = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections | undefined,
): { slotIndex: number; study: { id: string; author: string } } | undefined => {
	if (!courseSelections) return undefined
	const studies = getCourseOptionGroups(course)[0]?.studies ?? []
	for (const [slotIndexRaw, slot] of Object.entries(courseSelections)) {
		if (!slot) continue
		const slotIndex = Number(slotIndexRaw)
		for (const id of slot.workIds) {
			const study = studies.find((s) => s.id === id)
			if (study) {
				return {
					slotIndex,
					study: { id: study.id, author: study.author },
				}
			}
		}
	}
	return undefined
}

export const isCourseInteracted = (
	courseSelections: CourseSelections | undefined,
): boolean => !isSelectionStateEmpty(courseSelections)

export const courseCodenameMatchesDeepLink = (
	courseCodename: string,
	deepLink: string,
): boolean => {
	if (!courseCodename || !deepLink) return false
	const alnum = (s: string): string =>
		s.toLowerCase().replace(/[^a-z0-9]/g, '')
	return alnum(courseCodename) === alnum(deepLink)
}

export const courseMatchesDeepLink = (
	courseTitle: string,
	deepLink: string,
): boolean => {
	if (!deepLink) return false
	// Punctuation-insensitive: reduce both to lowercase alphanumeric-only so a
	// title with special chars (e.g. "English EAL/D") matches the syllabus
	// codename ("english_eal_d_…" / "english_eald_…") — both become "eald".
	const alnum = (s: string): string =>
		s.toLowerCase().replace(/[^a-z0-9]/g, '')
	const title = alnum(courseTitle)
	const code = alnum(deepLink)
	const codeNoYear = code.replace(/\d+$/u, '')
	if (codeNoYear && title.includes(codeNoYear)) return true
	return title.includes(code) || code.includes(title)
}

/**
 * Build the "{title} ({years})" string used by all GA payloads.
 */
export const formatPrescriptionsListLabel = (
	title: string | undefined,
	years: string | undefined,
): string => {
	if (!title) return ''
	if (!years) return title
	return `${title} (${years})`
}

export const safeRandomUUID = (): string => {
	if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
		return crypto.randomUUID()
	}
	return ''
}

//TODO confirm the correct focus area id
export const EMPTY_FOCUS_AREA_GUID =
	'00000000-0000-0000-0000-000000000000' as const

export const SELECTION_FETCH_ERROR_MESSAGE =
	"We couldn't load text prescription selections. Please try again." as const

export const SELECT_PLACEHOLDER_TEXT = 'Select prescription' as const

export const AUTHOR_STUDY_LABEL = 'Author study' as const

export const resolveFocusAreaId = (
	selector: PrescriptionSelectionFocusAreaSelector | undefined,
): string => selector?.['focus-area-cmsid']?.trim() || EMPTY_FOCUS_AREA_GUID

export const getSelectorAtIndex = (
	course: PrescriptionSelectionCourse,
	slotIndex: number,
): PrescriptionSelectionFocusAreaSelector | undefined =>
	course.selectors?.[slotIndex]
